import { useReducer, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import { getUserTransactionsRest, exportUserTransactionsCsvRest } from '@/services/transactions/transactions-service';
import TransactionsTable from '@/pages/common/TransactionsTable';
import DebouncedInput from '@/components/DebounceInput';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { TransactionDto } from '@repo/common-types';
import CopyField from '@/common/forms/CopyField';
import { config } from '@repo/common-types';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import { getStatusBadgeColor } from '@/pages/deposits/utils/get-status-badge-color';
import ROUTES from '@/routes/routes-enums';
import { trimHash } from '@/common/utils/trim-hash';
import CopyFieldWrapper from '@/components/CopyFieldWrapper';

import {
  tableTransactionsQueryReducer,
  initialState,
  TableTransactionsActionType,
} from '../user-transactions-query-reducer';
const columns = (navigate): ColumnDef<TransactionDto>[] => [
  {
    accessorKey: 'createdAt',
    header: 'Date',
    cell: (info) => format(info.getValue() as Date, 'dd MMM yyyy HH:mm'),
    sortDescFirst: false,
    enableSorting: true,
  },
  {
    accessorKey: 'txId',
    header: 'Transaction ID',
    sortUndefined: 'last',
    cell: ({ row }) => {
      const cellData = row.original.txId ?? '';
      const displayAddress = trimHash(cellData);
      return (
        <CopyFieldWrapper value={cellData}>
          <a
            href={`${config.deposits.polygon.blockExplorerUrl}/tx/${cellData}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-underline cursor-pointer"
          >
            {displayAddress}
          </a>
        </CopyFieldWrapper>
      );
    },
    size: 200,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const transaction = row.original;

      // Check if it's a tournament-related transaction
      const tournamentTypes = [
        'TOURNAMENT_REGISTRATION',
        'TOURNAMENT_UNREGISTRATION',
        'TOURNAMENT_PRIZE',
        'TOURNAMENT_ELIMINATE_PLAYER',
        'TOURNAMENT_REFUND',
        'TOURNAMENT_KO_PAYOUT',
        'TOURNAMENT_REBUY',
        'TOURNAMENT_ADDON',
        'TOURNAMENT_CLOSE_REGISTRATION',
        'TOURNAMENT_COLLECT_RAKE',
        'TOURNAMENT_DEPLOY',
        'TOURNAMENT_END',
        'TOURNAMENT_EXTRACT_FUNDS',
        'TOURNAMENT_FUND_PRIZE_POOL',
        'TOURNAMENT_PAUSE',
        'TOURNAMENT_START',
        'TOURNAMENT_UNPAUSE',
      ];

      if (tournamentTypes.includes(transaction.type) && transaction.tournamentId) {
        // Display tournament name if available, otherwise show tournament ID
        const displayName = transaction.tournamentName || `Tournament ${transaction.tournamentId.slice(0, 8)}...`;
        return (
          <a
            onClick={() => navigate(`${ROUTES.TOURNAMENT_DETAILS}/${transaction.tournamentId}`)}
            className="text-decoration-underline text-muted cursor-pointer"
          >
            {displayName}
          </a>
        );
      }

      // Display table name for table transactions
      return transaction.tableName ? (
        <a
          onClick={() => navigate(`${ROUTES.TABLE}/${transaction.tableId}`)}
          className="text-decoration-underline cursor-pointer"
        >
          <span>{transaction.tableName}</span>
        </a>
      ) : null;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    enableSorting: true,
    cell: ({ row }) => (
      <span className={`badge ${getStatusBadgeColor(row.original.status)}`}>{row.original.status}</span>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: true,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    cell: (info) => <div className="text-center">{`${Number(info.getValue() || 0).toFixed(2)}`}</div>,
    enableSorting: true,
    meta: {
      className: 'justify-content-center',
    },
  },
  {
    accessorKey: 'addressFrom',
    header: 'From',
    cell: ({ row }) => {
      const cellData = row.original.addressFrom ?? '';
      const displayAddress = trimHash(cellData);
      return (
        displayAddress && (
          <CopyFieldWrapper value={cellData}>
            <a
              href={`${config.deposits.polygon.blockExplorerUrl}/address/${cellData}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-underline cursor-pointer"
            >
              {displayAddress}
            </a>
          </CopyFieldWrapper>
        )
      );
    },
    size: 200,
  },
  {
    accessorKey: 'addressTo',
    header: 'To',
    cell: ({ row }) => {
      const cellData = row.original.addressTo ?? '';
      const displayAddress = trimHash(cellData);
      return (
        displayAddress && (
          <CopyFieldWrapper value={cellData}>
            <a
              href={`${config.deposits.polygon.blockExplorerUrl}/address/${cellData}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-underline cursor-pointer"
            >
              {displayAddress}
            </a>
          </CopyFieldWrapper>
        )
      );
    },
    size: 200,
  },
];

const TransactionsSection = ({ userId }: { userId: string }) => {
  const navigate = useNavigate();
  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);

  const [queryParams, dispatch] = useReducer(tableTransactionsQueryReducer, initialState);

  const { sorting, filter, take, skip } = queryParams;

  const {
    data: transactionsResponse,
    isLoading: isTransactionsLoading,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: ['userTransactions', userId, queryParams],
    queryFn: () => getUserTransactionsRest(userId, { sorts: convertSortingToApiFormat(sorting), filter, take, skip }),
  });
  const handlePageChange = useCallback((skip: number, take: number) => {
    dispatch({ type: TableTransactionsActionType.SET_PAGINATION, payload: { skip, take } });
  }, []);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(queryParams.sorting) : updater;
      dispatch({ type: TableTransactionsActionType.SET_SORTING, payload: newSorting });
    },
    [queryParams.sorting],
  );

  const handleSearch = useCallback((value: string | number) => {
    setIsDebouncing(true);
    dispatch({ type: TableTransactionsActionType.SET_SEARCH, payload: value.toString() });
    setIsDebouncing(false);
  }, []);

  const handleExportTransactions = useCallback(async () => {
    try {
      await exportUserTransactionsCsvRest(userId);
    } catch (error) {
      console.error('Error exporting transactions:', error);
    }
  }, [userId]);

  const renderTransactionsTable = useMemo(() => {
    return (
      <TransactionsTable
        transactions={transactionsResponse?.transactions || []}
        refreshTable={refetchTransactions}
        totalCount={transactionsResponse?.totalCount || 0}
        onPageChange={handlePageChange}
        take={queryParams.take}
        skip={queryParams.skip}
        isLoadingData={isTransactionsLoading}
        onSortingChange={handleSortingChange}
        columns={columns(navigate)}
        sorting={queryParams.sorting}
      />
    );
  }, [transactionsResponse?.transactions, queryParams.take, queryParams.skip]);

  return (
    <div className="my-4">
      <div className="d-flex align-items-center flex-row gap-3">
        <h3>Transactions</h3>
        <DebouncedInput
          className="form-control w-auto"
          placeholder="Search transactions..."
          onChange={handleSearch}
          value={queryParams.filter?.search || ''}
          isLoading={isDebouncing}
        />
        {Object.values(queryParams.filter || {}).some(Boolean) && (
          <Button
            size={ButtonSizes.sm}
            variant={ButtonVariants.primary}
            onClick={() => dispatch({ type: TableTransactionsActionType.RESET_FILTERS })}
            className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
          >
            Clear Filters
          </Button>
        )}
      </div>
      {renderTransactionsTable}
      <div className="d-flex justify-content-end mt-3">
        <Button
          size={ButtonSizes.sm}
          variant={ButtonVariants.secondary}
          onClick={handleExportTransactions}
          className="btn btn-outline d-flex align-items-center justify-content-center gap-2"
        >
          Export transactions
        </Button>
      </div>
    </div>
  );
};

export default TransactionsSection;
